import { useQuery } from '@tanstack/react-query';
import type { Acao, Recurso } from '@arena/shared/permissoes';
import { useAtrasado } from '../agenda/useAtrasado.js';
import type { NomeDeIcone } from '../ui/Icone.js';

/**
 * A busca global da topbar (documento 05, tarefa 11).
 *
 * O campo único da topbar precisa responder por "aluno, comanda ou
 * reserva" — o placeholder já promete isso — sem que `shell/` conheça
 * `alunos`, `comanda` ou `agenda`. A saída é a MESMA do backend para o
 * mesmo problema (`EscopoRegistry` em `access/`): um REGISTRY. Cada
 * módulo de domínio registra a própria fonte, uma vez, por efeito
 * colateral de importar o arquivo que a declara — `alunos/buscaGlobal.ts`
 * é a referência viva do padrão.
 *
 * Esta entrega é só a MOLDURA MÍNIMA e a fonte de `alunos`. Os módulos
 * seguintes (comanda, faturas…) registram a própria fonte do mesmo jeito:
 *
 *     registrarFonteDeBusca({
 *       chave: 'comanda',
 *       rotulo: 'Comandas',
 *       exige: { recurso: 'comanda', acao: 'ler' },
 *       buscar: (termo) => comandaApi.buscarGlobal(termo),
 *     });
 *
 * e importam o arquivo uma vez em `App.tsx`, ao lado do de alunos.
 */

export interface ResultadoDeBusca {
  readonly id: string;
  readonly titulo: string;
  readonly subtitulo?: string;
  readonly icone: NomeDeIcone;
  /** Rota do React Router para onde o clique leva. */
  readonly rota: string;
}

export interface FonteDeBusca {
  /** Único no registry — também a `key` React do grupo de resultados. */
  readonly chave: string;
  /** O rótulo do grupo, no painel de resultados. */
  readonly rotulo: string;
  /** O que a matriz precisa liberar para esta fonte ser consultada. */
  readonly exige: { recurso: Recurso; acao: Acao };
  readonly buscar: (termo: string) => Promise<readonly ResultadoDeBusca[]>;
}

const fontes: FonteDeBusca[] = [];

/** Chamado uma vez por módulo, no arquivo que declara a fonte dele. */
export function registrarFonteDeBusca(fonte: FonteDeBusca): void {
  // Idempotente: o hot-reload do Vite reimporta o módulo sem recarregar
  // a página, e duplicar a fonte no array duplicaria cada resultado dela.
  const indice = fontes.findIndex((f) => f.chave === fonte.chave);
  if (indice >= 0) fontes[indice] = fonte;
  else fontes.push(fonte);
}

/** Menos que isto não busca — "a" ou "62" devolveria a base inteira. */
export const MINIMO_DE_CARACTERES_NA_BUSCA = 2;

export interface GrupoDeResultados {
  readonly fonte: FonteDeBusca;
  readonly itens: readonly ResultadoDeBusca[];
}

/**
 * Consulta as fontes que o perfil pode ver, em paralelo, e devolve só os
 * grupos com resultado. A verificação de permissão aqui é a mesma
 * ressalva de sempre: COSMÉTICA — esconde o grupo da Gestão que não tem
 * `comanda:ler`; o servidor de cada fonte já filtra pelo escopo dele.
 */
export function useBuscaGlobal(
  termo: string,
  pode: (recurso: Recurso, acao: Acao) => boolean,
): { carregando: boolean; grupos: readonly GrupoDeResultados[] } {
  const atrasado = useAtrasado(termo.trim(), 250);
  const habilitada = atrasado.length >= MINIMO_DE_CARACTERES_NA_BUSCA;

  const consulta = useQuery({
    queryKey: ['busca-global', atrasado, fontes.map((f) => f.chave)],
    queryFn: async (): Promise<GrupoDeResultados[]> => {
      const disponiveis = fontes.filter((f) => pode(f.exige.recurso, f.exige.acao));
      const grupos = await Promise.all(
        disponiveis.map(async (fonte) => ({ fonte, itens: await fonte.buscar(atrasado) })),
      );
      return grupos.filter((g) => g.itens.length > 0);
    },
    enabled: habilitada,
    staleTime: 15_000,
  });

  return { carregando: habilitada && consulta.isFetching, grupos: consulta.data ?? [] };
}
